import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tabs,
  Tab,
  FormControlLabel,
  Divider,
  Backdrop,
  LinearProgress,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { apiService } from '../services/apiService';

const COLOR_LEGEND = [
  { range: '100%',    bg: '#4CAF50', text: '#fff',     label: 'Completo' },
  { range: '50–99%',  bg: '#C8E6C9', text: '#1b5e20', label: 'Mayoría' },
  { range: '1–49%',   bg: '#FFE0B2', text: '#e65100', label: 'Parcial' },
  { range: '0%',      bg: '#FFCDD2', text: '#b71c1c', label: 'Sin implementar' },
];

const CampaignTracker = ({ showSnackbar, selectedCampaignId, onCampaignSelected, autoOpenNew }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [countries, setCountries] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [channels, setChannels] = useState([]);
  const [trackingData, setTrackingData] = useState({});
  const [countryChannels, setCountryChannels] = useState({}); // Mapeo país -> canales activos
  const [campaignChannels, setCampaignChannels] = useState({}); // Mapeo campaña -> canales activos
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [openNewCampaign, setOpenNewCampaign] = useState(false);
  const [openEditCampaign, setOpenEditCampaign] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [campaignDialogTab, setCampaignDialogTab] = useState(0);
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [updatingCampaign, setUpdatingCampaign] = useState(false);
  const [pendingValues, setPendingValues] = useState({});
  const [newCampaignData, setNewCampaignData] = useState({
    campaign_name: 'ToolBox - ',
    campaign_type: 'General',
    description: '',
    start_date: '',
    end_date: '',
    selected_countries: [],
    selected_channels: []
  });

  useEffect(() => {
    loadData();
  }, []);

  // Efecto para seleccionar campaña cuando viene desde CampaignsList (onCampaignSelected estable en padre)
  useEffect(() => {
    if (selectedCampaignId && campaigns.length > 0) {
      setSelectedCampaign(selectedCampaignId);
      if (onCampaignSelected) {
        onCampaignSelected();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- solo reaccionar a selectedCampaignId/campaigns
  }, [selectedCampaignId, campaigns]);

  // Efecto para abrir diálogo de creación automáticamente
  useEffect(() => {
    if (autoOpenNew) {
      setOpenNewCampaign(true);
      setCampaignDialogTab(0);
    }
  }, [autoOpenNew]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [countriesRes, campaignsRes, channelsRes, trackingRes, countryChannelsRes, campaignChannelsRes] = await Promise.all([
        apiService.getCountries(),
        apiService.getCampaigns(),
        apiService.getChannels(),
        apiService.getTrackingData(),
        apiService.getCountryChannels(),
        apiService.getCampaignChannels()
      ]);

      if (countriesRes.success) setCountries(countriesRes.countries);
      if (campaignsRes.success) setCampaigns(campaignsRes.campaigns);
      if (channelsRes.success) setChannels(channelsRes.channels);
      if (trackingRes.success) {
        const trackingMap = {};
        trackingRes.tracking_data.forEach(item => {
          const key = `${item.country_code}-${item.campaign_id}-${item.channel_id}`;
          trackingMap[key] = item.implementation_percentage ?? 0;
        });
        setTrackingData(trackingMap);
      }
      
      // Procesar country_channels
      if (countryChannelsRes.success) {
        const countryChannelsMap = {};
        countryChannelsRes.country_channels.forEach(item => {
          if (!countryChannelsMap[item.country_code]) {
            countryChannelsMap[item.country_code] = new Set();
          }
          if (item.is_active) {
            countryChannelsMap[item.country_code].add(item.channel_id);
          }
        });
        // Convertir Sets a Arrays para facilitar uso
        const countryChannelsObj = {};
        Object.keys(countryChannelsMap).forEach(countryCode => {
          countryChannelsObj[countryCode] = Array.from(countryChannelsMap[countryCode]);
        });
        setCountryChannels(countryChannelsObj);
      }
      
      // Procesar campaign_channels (incluir canales con is_active truthy: true, 1, 'true')
      if (campaignChannelsRes.success) {
        const campaignChannelsMap = {};
        campaignChannelsRes.campaign_channels.forEach(item => {
          const cid = String(item.campaign_id);
          if (!campaignChannelsMap[cid]) {
            campaignChannelsMap[cid] = new Set();
          }
          const isActive = item.is_active === true || item.is_active === 1 || item.is_active === 'true';
          if (isActive) {
            campaignChannelsMap[cid].add(item.channel_id);
          }
        });
        // Convertir Sets a Arrays
        const campaignChannelsObj = {};
        Object.keys(campaignChannelsMap).forEach(campaignId => {
          campaignChannelsObj[campaignId] = Array.from(campaignChannelsMap[campaignId]);
        });
        setCampaignChannels(campaignChannelsObj);
      }
      
      setLoading(false);
    } catch (err) {
      setError('Error al cargar los datos');
      setLoading(false);
    }
  };

  const handlePercentageChange = (countryCode, campaignId, channelId, value) => {
    const key = `${countryCode}-${campaignId}-${channelId}`;
    const num = parseInt(value, 10);
    const clamped = isNaN(num) ? 0 : Math.min(100, Math.max(0, num));
    setPendingValues(prev => ({ ...prev, [key]: clamped }));
  };

  const handlePercentageBlur = async (countryCode, campaignId, channelId) => {
    const key = `${countryCode}-${campaignId}-${channelId}`;
    if (!(key in pendingValues)) return;
    const newValue = pendingValues[key];
    const previousValue = trackingData[key] ?? 0;
    setPendingValues(prev => { const n = { ...prev }; delete n[key]; return n; });
    setTrackingData(prev => ({ ...prev, [key]: newValue }));
    try {
      await apiService.updateTracking({
        country_code: countryCode,
        campaign_id: campaignId,
        channel_id: channelId,
        implementation_percentage: newValue
      });
      showSnackbar('Estado actualizado correctamente');
    } catch (err) {
      setTrackingData(prev => ({ ...prev, [key]: previousValue }));
      showSnackbar('Error al actualizar el estado', 'error');
    }
  };

  const getTrackingStatus = (countryCode, campaignId, channelId) => {
    const key = `${countryCode}-${campaignId}-${channelId}`;
    return trackingData[key] ?? 0;
  };

  const getCellBgColor = (value) => {
    if (value === 100) return '#4CAF50';
    if (value >= 50)  return '#C8E6C9';
    if (value >= 1)   return '#FFE0B2';
    return '#FFCDD2';
  };

  const handleCreateCampaign = async () => {
    try {
      // Validar que haya al menos un país y un canal seleccionado
      if (newCampaignData.selected_countries.length === 0) {
        showSnackbar('Debes seleccionar al menos un país', 'error');
        return;
      }
      if (newCampaignData.selected_channels.length === 0) {
        showSnackbar('Debes seleccionar al menos un canal', 'error');
        return;
      }

      setCreatingCampaign(true);

      // Crear la campaña
      const campaignData = {
        campaign_name: newCampaignData.campaign_name,
        campaign_type: newCampaignData.campaign_type,
        description: newCampaignData.description,
        start_date: newCampaignData.start_date,
        end_date: newCampaignData.end_date
      };

      const response = await apiService.createCampaign(campaignData);
      if (response.success) {
        const campaignId = response.campaign_id;
        
        // Configurar canales para la campaña
        for (const channelId of newCampaignData.selected_channels) {
          await apiService.updateCampaignChannel({
            campaign_id: campaignId,
            channel_id: channelId,
            is_active: true
          });
        }
        
                showSnackbar('ToolBox creada correctamente');
        if (newCampaignData.selected_countries?.length > 0 && !newCampaignData.selected_channels?.length) {
          showSnackbar('Para poder ver datos por favor añade canales a este ToolBox', 'warning');
        }
        setOpenNewCampaign(false);
        setCampaignDialogTab(0);
        setNewCampaignData({
          campaign_name: 'ToolBox - ',
          campaign_type: 'General',
          description: '',
          start_date: '',
          end_date: '',
          selected_countries: [],
          selected_channels: []
        });
        // Recargar datos para incluir la nueva campaña
        await loadData();
      }
    } catch (err) {
      showSnackbar('Error al crear la ToolBox', 'error');
    } finally {
      setCreatingCampaign(false);
    }
  };

  const handleToggleCountry = (countryCode) => {
    setNewCampaignData(prev => {
      const current = prev.selected_countries || [];
      if (current.includes(countryCode)) {
        return { ...prev, selected_countries: current.filter(c => c !== countryCode) };
      } else {
        return { ...prev, selected_countries: [...current, countryCode] };
      }
    });
  };

  const handleToggleChannel = (channelId) => {
    setNewCampaignData(prev => {
      const current = prev.selected_channels || [];
      if (current.includes(channelId)) {
        return { ...prev, selected_channels: current.filter(c => c !== channelId) };
      } else {
        return { ...prev, selected_channels: [...current, channelId] };
      }
    });
  };

  const handleSelectAllCountries = () => {
    const allCountries = getFilteredCountries().map(c => c.code);
    setNewCampaignData(prev => ({
      ...prev,
      selected_countries: allCountries
    }));
  };

  const handleDeselectAllCountries = () => {
    setNewCampaignData(prev => ({
      ...prev,
      selected_countries: []
    }));
  };

  const handleSelectAllChannels = () => {
    const allChannels = channels.map(c => c.id);
    setNewCampaignData(prev => ({
      ...prev,
      selected_channels: allChannels
    }));
  };

  const handleDeselectAllChannels = () => {
    setNewCampaignData(prev => ({
      ...prev,
      selected_channels: []
    }));
  };

  const handleCampaignChange = (event) => {
    setSelectedCampaign(event.target.value);
  };

  const handleRegionChange = (event) => {
    setSelectedRegion(event.target.value);
  };

  // Definir regiones
  const regions = [
    { value: 'all', label: 'Todas las Regiones' },
    { value: 'cono_sur', label: 'Cono Sur', countries: ['AR', 'CL', 'UY', 'PY','VE','PE','CO','EC','UY','BO'] },
    { value: 'centro_america', label: 'Centro América', countries: ['MX', 'GT', 'BZ', 'SV', 'HN', 'NI', 'CR', 'PA', 'DO'] }
  ];

  // Obtener región de acceso del usuario desde localStorage
  const getUserRegionAccess = () => {
    const userData = localStorage.getItem('userData');
    if (userData) {
      const user = JSON.parse(userData);
      return user.region_access || 'all';
    }
    return 'all';
  };



  // Países que tienen al menos una fila en tracking para esta campaña (activa o inactiva).
  // Así al editar la toolbox se cargan bien los países ya asociados y al guardar se calculan bien los que agregar/quitar.
  const getCountriesForCampaign = (campaignId) => {
    const countryCodes = new Set();
    Object.keys(trackingData).forEach(key => {
      const [countryCode, campId] = key.split('-');
      if (campId === campaignId) {
        countryCodes.add(countryCode);
      }
    });
    return Array.from(countryCodes);
  };

  // Filtrar países por región y país específico
  const getFilteredCountries = () => {
    const userRegionAccess = getUserRegionAccess();
    let filteredCountries = countries;
    
    // Si hay una campaña seleccionada, filtrar solo países que tienen tracking_data para esa campaña
    if (selectedCampaign !== 'all') {
      const campaignCountryCodes = getCountriesForCampaign(selectedCampaign);
      filteredCountries = countries.filter(country => campaignCountryCodes.includes(country.code));
    }
    
    // Si el usuario tiene acceso a todas las regiones, usar el filtro seleccionado
    if (userRegionAccess === 'all') {
      if (selectedRegion !== 'all') {
        const region = regions.find(r => r.value === selectedRegion);
        if (region && region.countries) {
          filteredCountries = filteredCountries.filter(country => region.countries.includes(country.code));
        }
      }
    } else {
      // Si el usuario tiene acceso limitado, aplicar automáticamente su región
      if (userRegionAccess === 'cono_sur') {
        const conoSurCountries = ['AR', 'CL', 'UY', 'PY', 'VE', 'PE', 'CO', 'EC', 'BO'];
        filteredCountries = filteredCountries.filter(country => conoSurCountries.includes(country.code));
      }
      
      if (userRegionAccess === 'centro_america') {
        const centroAmericaCountries = ['MX', 'GT', 'BZ', 'SV', 'HN', 'NI', 'CR', 'PA', 'DO'];
        filteredCountries = filteredCountries.filter(country => centroAmericaCountries.includes(country.code));
      }
    }
    
    // Aplicar filtro por país específico
    if (selectedCountry !== 'all') {
      filteredCountries = filteredCountries.filter(country => country.code === selectedCountry);
    }
    
    return filteredCountries;
  };

  // Obtener canales válidos para un país y campaña (intersección de mapeos)
  const getValidChannels = (countryCode, campaignId) => {
    if (selectedCampaign === 'all') {
      // Si no hay campaña seleccionada, solo filtrar por país
      const countryChans = countryChannels[countryCode] || [];
      return channels.filter(ch => countryChans.includes(ch.id));
    } else {
      // Filtrar por país Y campaña
      const countryChans = countryChannels[countryCode] || [];
      const campaignChans = campaignChannels[campaignId] || [];
      const validChannelIds = countryChans.filter(id => campaignChans.includes(id));
      return channels.filter(ch => validChannelIds.includes(ch.id));
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const currentCampaign = campaigns.find(c => c.id === selectedCampaign);
  const filteredCountries = getFilteredCountries();

  return (
    <Box>
      <Backdrop open={creatingCampaign || updatingCampaign} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box 
        display="flex" 
        flexDirection={isMobile ? 'column' : 'row'}
        justifyContent="space-between" 
        alignItems={isMobile ? 'flex-start' : 'center'} 
        sx={{ mb: { xs: 2, sm: 3 }, gap: { xs: 2, sm: 0 } }}
      >
        <Typography variant={isMobile ? 'h5' : 'h4'}>
          Tracker de ToolBox
        </Typography>
        <Box display="flex" gap={{ xs: 1, sm: 2 }} flexDirection={isMobile ? 'column' : 'row'} width={isMobile ? '100%' : 'auto'}>
          {currentCampaign && selectedCampaign !== 'all' && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={async () => {
                setEditingCampaign(currentCampaign);
                // Cargar canales actuales de la campaña
                try {
                  const channelsRes = await apiService.getCampaignChannels(currentCampaign.id);
                  const activeChannels = channelsRes.success 
                    ? channelsRes.campaign_channels
                        .filter(cc => cc.campaign_id === currentCampaign.id && cc.is_active)
                        .map(cc => cc.channel_id)
                    : [];
                  
                  // Cargar países actuales de la campaña desde trackingData
                  const currentCountryCodes = getCountriesForCampaign(currentCampaign.id);
                  
                  setNewCampaignData({
                    campaign_name: currentCampaign.name,
                    campaign_type: currentCampaign.type,
                    description: currentCampaign.description || '',
                    start_date: currentCampaign.start_date ? currentCampaign.start_date.split('T')[0] : '',
                    end_date: currentCampaign.end_date ? currentCampaign.end_date.split('T')[0] : '',
                    selected_countries: Array.isArray(currentCountryCodes) ? currentCountryCodes : [],
                    selected_channels: Array.isArray(activeChannels) ? activeChannels : []
                  });
                  setOpenEditCampaign(true);
                  setCampaignDialogTab(0);
                } catch (err) {
                  showSnackbar('Error al cargar datos de la ToolBox', 'error');
                }
              }}
              fullWidth={isMobile}
              size={isMobile ? 'medium' : 'large'}
            >
              Editar ToolBox
            </Button>
          )}
        </Box>
      </Box>

      {/* Filtros */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 2, sm: 3 } }}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Seleccionar ToolBox</InputLabel>
            <Select
              value={selectedCampaign}
              label="Seleccionar ToolBox"
              onChange={handleCampaignChange}
            >
              <MenuItem value="all">
                <em>Selecciona una ToolBox</em>
              </MenuItem>
              {campaigns.map((campaign) => (
                <MenuItem key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {/* Solo mostrar filtro de región si el usuario tiene acceso completo */}
        {getUserRegionAccess() === 'all' && (
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Filtrar por Región</InputLabel>
              <Select
                value={selectedRegion}
                label="Filtrar por Región"
                onChange={handleRegionChange}
              >
                {regions.map((region) => (
                  <MenuItem key={region.value} value={region.value}>
                    {region.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Filtrar por País</InputLabel>
            <Select
              value={selectedCountry}
              label="Filtrar por País"
              onChange={(event) => setSelectedCountry(event.target.value)}
            >
              <MenuItem value="all">Todos los países</MenuItem>
              {getFilteredCountries().map((country) => (
                <MenuItem key={country.code} value={country.code}>
                  {country.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {selectedCampaign !== 'all' && (
          <Grid item xs={12} md={6}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary">
                Países mostrados:
              </Typography>
              <Chip 
                label={getFilteredCountries().length} 
                color="primary" 
                size="small"
                sx={{ color: 'white' }}
              />
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Información de la campaña seleccionada */}
      {currentCampaign && selectedCampaign !== 'all' && (
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 3 } }}>
          <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom>
            {currentCampaign.name}
          </Typography>
          <Box 
            display="flex" 
            gap={{ xs: 1, sm: 2 }} 
            flexWrap="wrap"
            sx={{ 
              overflowX: 'auto',
              pb: { xs: 1, sm: 0 },
              '&::-webkit-scrollbar': {
                height: 4,
              },
            }}
          >
            <Chip 
              label={`Tipo: ${currentCampaign.type}`} 
              color="primary"
              sx={{ color: 'white' }}
            />
            {currentCampaign.start_date && (
              <Chip 
                label={`Inicio: ${new Date(currentCampaign.start_date).toLocaleDateString('es-ES')}`}
                color="primary"
                sx={{ color: 'white' }}
              />
            )}
            {currentCampaign.end_date && (
              <Chip 
                label={`Fin: ${new Date(currentCampaign.end_date).toLocaleDateString('es-ES')}`}
                color="primary"
                sx={{ color: 'white' }}
              />
            )}
          </Box>
          {currentCampaign.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {currentCampaign.description}
            </Typography>
          )}
        </Paper>
      )}

      {/* Barra de progreso promedio de la campaña seleccionada */}
      {selectedCampaign !== 'all' && filteredCountries.length > 0 && (() => {
        const allValues = filteredCountries.flatMap(country => {
          const validChannels = getValidChannels(country.code, currentCampaign.id);
          return validChannels.map(ch => {
            const key = `${country.code}-${currentCampaign.id}-${ch.id}`;
            return key in pendingValues ? pendingValues[key] : (trackingData[key] ?? 0);
          });
        });
        const avg = allValues.length > 0
          ? allValues.reduce((sum, v) => sum + v, 0) / allValues.length
          : 0;
        const barColor = avg >= 80 ? '#4CAF50' : avg >= 50 ? '#FF9800' : '#f44336';
        return (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Tooltip
                title="Promedio aritmético de todos los valores ingresados en las celdas visibles (filtro activo). Un canal con 0 baja el promedio."
                arrow
                placement="top"
              >
                <Box display="flex" alignItems="center" gap={0.5} sx={{ cursor: 'help' }}>
                  <Typography variant="body2" color="text.secondary">
                    Implementación promedio de la campaña
                  </Typography>
                  <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                </Box>
              </Tooltip>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: barColor }}>
                {avg.toFixed(1)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(avg, 100)}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: '#e0e0e0',
                '& .MuiLinearProgress-bar': { backgroundColor: barColor, borderRadius: 4 }
              }}
            />
          </Box>
        );
      })()}

      {/* Leyenda de colores */}
      {selectedCampaign !== 'all' && (
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
            Leyenda:
          </Typography>
          {COLOR_LEGEND.map(({ range, bg, text, label }) => (
            <Tooltip key={range} title={`${label} (${range})`} arrow placement="top">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  backgroundColor: bg,
                  cursor: 'default',
                }}
              >
                <Typography variant="caption" sx={{ color: text, fontWeight: 'bold', fontSize: 11 }}>
                  {range}
                </Typography>
                <Typography variant="caption" sx={{ color: text, fontSize: 11 }}>
                  {label}
                </Typography>
              </Box>
            </Tooltip>
          ))}
        </Box>
      )}

      {/* Tabla de tracking - Solo mostrar cuando hay una campaña seleccionada */}
      {selectedCampaign === 'all' ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Selecciona una ToolBox para ver su tracking
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Usa el selector de arriba para elegir una ToolBox y ver sus datos de implementación por país y canal
          </Typography>
        </Paper>
      ) : (
        <TableContainer 
          component={Paper}
          sx={{ 
            maxWidth: '100%',
            overflowX: 'auto',
            '& .MuiTable-root': {
              minWidth: 650
            }
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>País</TableCell>
                {(() => {
                  // Obtener canales válidos para la primera campaña filtrada (para mostrar columnas)
                  const firstCountry = filteredCountries[0];
                  const validChannels = firstCountry 
                    ? getValidChannels(firstCountry.code, currentCampaign?.id || '')
                    : channels;
                  return validChannels.map((channel) => (
                    <TableCell key={channel.id} align="center" sx={{ fontWeight: 'bold', minWidth: 120 }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {channel.name}
                        </Typography>
                        <Chip 
                          label={channel.type} 
                          size="small" 
                          color="default" 
                          variant="outlined"
                          sx={{ mb: 1, color: 'white', borderColor: 'white' }}
                        />
                      </Box>
                    </TableCell>
                  ));
                })()}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCountries.length > 0 ? (
                filteredCountries.map((country) => (
                  <TableRow key={country.code} hover>
                    <TableCell component="th" scope="row">
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          {country.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {country.code}
                        </Typography>
                      </Box>
                    </TableCell>
                    {(() => {
                      const validChannels = getValidChannels(country.code, currentCampaign.id);
                      return validChannels.map((channel) => {
                        const key = `${country.code}-${currentCampaign.id}-${channel.id}`;
                        const committedValue = getTrackingStatus(country.code, currentCampaign.id, channel.id);
                        const displayValue = key in pendingValues ? pendingValues[key] : committedValue;
                        return (
                          <TableCell
                            key={`${country.code}-${channel.id}`}
                            align="center"
                            sx={{ backgroundColor: getCellBgColor(committedValue), p: 0.5 }}
                          >
                            <Tooltip title={`${committedValue}% implementado`} placement="top">
                              <TextField
                                type="number"
                                value={displayValue}
                                inputProps={{ min: 0, max: 100, style: { textAlign: 'center', padding: '6px 4px' } }}
                                sx={{ width: 75 }}
                                size="small"
                                onChange={(e) => handlePercentageChange(country.code, currentCampaign.id, channel.id, e.target.value)}
                                onBlur={() => handlePercentageBlur(country.code, currentCampaign.id, channel.id)}
                              />
                            </Tooltip>
                          </TableCell>
                        );
                      });
                    })()}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={100} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No hay países asociados a esta ToolBox
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Diálogo para nueva campaña */}
      <Dialog 
        open={openNewCampaign} 
        onClose={() => setOpenNewCampaign(false)} 
        fullScreen={isMobile}
        maxWidth={isMobile ? 'xs' : 'md'} 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} justifyContent="space-between" alignItems={isMobile ? 'flex-start' : 'center'} gap={1}>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'}>Crear Nueva ToolBox</Typography>
            <Chip 
              label={`${newCampaignData.selected_channels?.length || 0} canales, ${newCampaignData.selected_countries?.length || 0} países`}
              color="primary"
              size="small"
            />
          </Box>
        </DialogTitle>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', overflowX: 'auto' }}>
          <Tabs 
            value={campaignDialogTab} 
            onChange={(e, newValue) => setCampaignDialogTab(newValue)}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons="auto"
          >
            <Tab label="Información" />
            <Tab label={`Canales (${newCampaignData.selected_channels?.length || 0})`} />
            <Tab label={`Países (${newCampaignData.selected_countries?.length || 0})`} />
          </Tabs>
        </Box>
        <DialogContent sx={{ minHeight: isMobile ? 'auto' : 400, p: { xs: 1.5, sm: 3 } }}>
          {campaignDialogTab === 0 && (
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="Nombre de la ToolBox"
                value={newCampaignData.campaign_name}
                onChange={(e) => {
                  // Si el usuario borra el prefijo, mantenerlo
                  let value = e.target.value;
                  if (!value.startsWith('ToolBox - ')) {
                    value = 'ToolBox - ' + value.replace(/^ToolBox - /, '');
                  }
                  setNewCampaignData({...newCampaignData, campaign_name: value});
                }}
                sx={{ mb: 2 }}
                required
                placeholder="ToolBox - "
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Tipo de ToolBox</InputLabel>
                <Select
                  value={newCampaignData.campaign_type}
                  label="Tipo de ToolBox"
                  onChange={(e) => setNewCampaignData({...newCampaignData, campaign_type: e.target.value})}
                >
                  <MenuItem value="Promocional">Promocional</MenuItem>
                  <MenuItem value="Estacional">Estacional</MenuItem>
                  <MenuItem value="Producto">Producto</MenuItem>
                  <MenuItem value="Digital">Digital</MenuItem>
                  <MenuItem value="Lanzamiento">Lanzamiento</MenuItem>
                  <MenuItem value="General">General</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Descripción"
                value={newCampaignData.description}
                onChange={(e) => setNewCampaignData({...newCampaignData, description: e.target.value})}
                multiline
                rows={3}
                sx={{ mb: 2 }}
              />
              <Box display="flex" gap={2}>
                <TextField
                  fullWidth
                  label="Fecha de Inicio"
                  type="date"
                  value={newCampaignData.start_date}
                  onChange={(e) => setNewCampaignData({...newCampaignData, start_date: e.target.value})}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  label="Fecha de Fin"
                  type="date"
                  value={newCampaignData.end_date}
                  onChange={(e) => setNewCampaignData({...newCampaignData, end_date: e.target.value})}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </Box>
          )}

          {campaignDialogTab === 1 && (
            <Box sx={{ pt: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">Canales de la ToolBox</Typography>
                <Box>
                  <Button size="small" onClick={handleSelectAllChannels} sx={{ mr: 1 }}>
                    Seleccionar Todos
                  </Button>
                  <Button size="small" onClick={handleDeselectAllChannels}>
                    Deseleccionar Todos
                  </Button>
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2} sx={{ maxHeight: 400, overflow: 'auto' }}>
                {channels.map((channel) => (
                  <Grid item xs={12} sm={6} md={4} key={channel.id}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newCampaignData.selected_channels?.includes(channel.id) || false}
                          onChange={() => handleToggleChannel(channel.id)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1">{channel.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {channel.type}
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {campaignDialogTab === 2 && (
            <Box sx={{ pt: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">Seleccionar Países</Typography>
                <Box>
                  <Button size="small" onClick={handleSelectAllCountries} sx={{ mr: 1 }}>
                    Seleccionar Todos
                  </Button>
                  <Button size="small" onClick={handleDeselectAllCountries}>
                    Deseleccionar Todos
                  </Button>
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ maxHeight: { xs: 300, sm: 400 }, overflow: 'auto' }}>
                {getFilteredCountries().map((country) => (
                  <Grid item xs={12} sm={6} md={4} key={country.code}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newCampaignData.selected_countries?.includes(country.code) || false}
                          onChange={() => handleToggleCountry(country.code)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1">{country.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {country.code}
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenNewCampaign(false);
            setCampaignDialogTab(0);
            setNewCampaignData({
              campaign_name: 'ToolBox - ',
              campaign_type: 'General',
              description: '',
              start_date: '',
              end_date: '',
              selected_countries: [],
              selected_channels: []
            });
          }}>
            Cancelar
          </Button>
          <Button 
            onClick={handleCreateCampaign} 
            variant="contained"
            disabled={!newCampaignData.campaign_name || 
                     !newCampaignData.selected_countries?.length || 
                     !newCampaignData.selected_channels?.length ||
                     creatingCampaign}
          >
            {creatingCampaign ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                Crear ToolBox
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo para editar campaña */}
      <Dialog 
        open={openEditCampaign} 
        onClose={() => setOpenEditCampaign(false)} 
        fullScreen={isMobile}
        maxWidth={isMobile ? 'xs' : 'md'} 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} justifyContent="space-between" alignItems={isMobile ? 'flex-start' : 'center'} gap={1}>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'}>Editar ToolBox</Typography>
            <Chip 
              label={`${newCampaignData.selected_channels?.length || 0} canales, ${newCampaignData.selected_countries?.length || 0} países`}
              color="primary"
              size="small"
            />
          </Box>
        </DialogTitle>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', overflowX: 'auto' }}>
          <Tabs 
            value={campaignDialogTab} 
            onChange={(e, newValue) => setCampaignDialogTab(newValue)}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons="auto"
          >
            <Tab label="Información" />
            <Tab label={`Canales (${newCampaignData.selected_channels?.length || 0})`} />
            <Tab label={`Países (${newCampaignData.selected_countries?.length || 0})`} />
          </Tabs>
        </Box>
        <DialogContent sx={{ minHeight: isMobile ? 'auto' : 400, p: { xs: 1.5, sm: 3 } }}>
          {campaignDialogTab === 0 && (
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="Nombre de la ToolBox"
                value={newCampaignData.campaign_name}
                onChange={(e) => {
                  // Si el usuario borra el prefijo, mantenerlo
                  let value = e.target.value;
                  if (!value.startsWith('ToolBox - ')) {
                    value = 'ToolBox - ' + value.replace(/^ToolBox - /, '');
                  }
                  setNewCampaignData({...newCampaignData, campaign_name: value});
                }}
                sx={{ mb: 2 }}
                required
                placeholder="ToolBox - "
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Tipo de ToolBox</InputLabel>
                <Select
                  value={newCampaignData.campaign_type}
                  label="Tipo de ToolBox"
                  onChange={(e) => setNewCampaignData({...newCampaignData, campaign_type: e.target.value})}
                >
                  <MenuItem value="Promocional">Promocional</MenuItem>
                  <MenuItem value="Estacional">Estacional</MenuItem>
                  <MenuItem value="Producto">Producto</MenuItem>
                  <MenuItem value="Digital">Digital</MenuItem>
                  <MenuItem value="Lanzamiento">Lanzamiento</MenuItem>
                  <MenuItem value="General">General</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Descripción"
                value={newCampaignData.description}
                onChange={(e) => setNewCampaignData({...newCampaignData, description: e.target.value})}
                multiline
                rows={3}
                sx={{ mb: 2 }}
              />
              <Box display="flex" gap={2}>
                <TextField
                  fullWidth
                  label="Fecha de Inicio"
                  type="date"
                  value={newCampaignData.start_date}
                  onChange={(e) => setNewCampaignData({...newCampaignData, start_date: e.target.value})}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  label="Fecha de Fin"
                  type="date"
                  value={newCampaignData.end_date}
                  onChange={(e) => setNewCampaignData({...newCampaignData, end_date: e.target.value})}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </Box>
          )}

          {campaignDialogTab === 1 && (
            <Box sx={{ pt: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">Canales de la ToolBox</Typography>
                <Box>
                  <Button size="small" onClick={handleSelectAllChannels} sx={{ mr: 1 }}>
                    Seleccionar Todos
                  </Button>
                  <Button size="small" onClick={handleDeselectAllChannels}>
                    Deseleccionar Todos
                  </Button>
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2} sx={{ maxHeight: 400, overflow: 'auto' }}>
                {channels.map((channel) => (
                  <Grid item xs={12} sm={6} md={4} key={channel.id}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newCampaignData.selected_channels?.includes(channel.id) || false}
                          onChange={() => handleToggleChannel(channel.id)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1">{channel.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {channel.type}
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {campaignDialogTab === 2 && (
            <Box sx={{ pt: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">Seleccionar Países</Typography>
                <Box>
                  <Button size="small" onClick={() => {
                    const allCountries = countries.map(c => c.code);
                    setNewCampaignData(prev => ({
                      ...prev,
                      selected_countries: allCountries
                    }));
                  }} sx={{ mr: 1 }}>
                    Seleccionar Todos
                  </Button>
                  <Button size="small" onClick={handleDeselectAllCountries}>
                    Deseleccionar Todos
                  </Button>
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ maxHeight: { xs: 300, sm: 400 }, overflow: 'auto' }}>
                {countries.map((country) => (
                  <Grid item xs={12} sm={6} md={4} key={country.code}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newCampaignData.selected_countries?.includes(country.code) || false}
                          onChange={() => handleToggleCountry(country.code)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1">{country.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {country.code}
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenEditCampaign(false);
            setCampaignDialogTab(0);
            setEditingCampaign(null);
          }}>
            Cancelar
          </Button>
          <Button 
            onClick={async () => {
              try {
                setUpdatingCampaign(true);
                
                // Actualizar información de la campaña
                await apiService.updateCampaign(editingCampaign.id, {
                  campaign_name: newCampaignData.campaign_name,
                  campaign_type: newCampaignData.campaign_type,
                  description: newCampaignData.description,
                  start_date: newCampaignData.start_date,
                  end_date: newCampaignData.end_date
                });
                
                // Actualizar países
                const currentCountryCodes = getCountriesForCampaign(editingCampaign.id);
                const newCountryCodes = newCampaignData.selected_countries || [];
                
                // Obtener canales activos de la campaña
                const activeChannels = campaignChannels[editingCampaign.id] || [];
                
                // Países a agregar
                const countriesToAdd = newCountryCodes.filter(code => !currentCountryCodes.includes(code));
                // Países a remover
                const countriesToRemove = currentCountryCodes.filter(code => !newCountryCodes.includes(code));
                
                // Agregar países: crear registros de tracking_data para cada país-canal
                for (const countryCode of countriesToAdd) {
                  for (const channelId of activeChannels) {
                    // Verificar que el país tenga el canal activo y la campaña tenga el canal activo
                    const countryHasChannel = (countryChannels[countryCode] || []).includes(channelId);
                    if (countryHasChannel) {
                      await apiService.updateTracking({
                        country_code: countryCode,
                        campaign_id: editingCampaign.id,
                        channel_id: channelId,
                        implementation_percentage: 0
                      });
                    }
                  }
                }
                
                // Remover países: eliminar todas las filas de tracking para ese país-campaña
                // (así dejan de devolverse en GET y el país ya no aparece en la toolbox)
                for (const countryCode of countriesToRemove) {
                  await apiService.deleteTrackingByCountryCampaign(countryCode, editingCampaign.id);
                }
                
                // Actualizar canales
                const currentChannels = campaignChannels[editingCampaign.id] || [];
                const newChannels = newCampaignData.selected_channels || [];
                
                // Desactivar canales que ya no están seleccionados
                for (const channelId of currentChannels) {
                  if (!newChannels.includes(channelId)) {
                    await apiService.updateCampaignChannel({
                      campaign_id: editingCampaign.id,
                      channel_id: channelId,
                      is_active: false
                    });
                  }
                }
                
                // Activar canales nuevos
                for (const channelId of newChannels) {
                  if (!currentChannels.includes(channelId)) {
                    await apiService.updateCampaignChannel({
                      campaign_id: editingCampaign.id,
                      channel_id: channelId,
                      is_active: true
                    });
                    // Asegurar que el canal esté activo para cada país de la campaña (country_channels)
                    // y crear tracking_data; así el canal aparece en la grilla aunque antes no estuviera en el país
                    for (const countryCode of newCountryCodes) {
                      try {
                        await apiService.updateCountryChannel({
                          country_code: countryCode,
                          channel_id: channelId,
                          is_active: true
                        });
                        await apiService.updateTracking({
                          country_code: countryCode,
                          campaign_id: editingCampaign.id,
                          channel_id: channelId,
                          implementation_percentage: 0
                        });
                      } catch (err) {
                        console.warn(`No se pudo asociar canal ${channelId} al país ${countryCode}:`, err?.response?.data?.error || err.message);
                      }
                    }
                  }
                }
                
                showSnackbar('Campaña actualizada correctamente');
                if (newCampaignData.selected_countries?.length > 0 && !newCampaignData.selected_channels?.length) {
                  showSnackbar('Para poder ver datos por favor añade canales a este ToolBox', 'warning');
                }
                await loadData();
                setOpenEditCampaign(false);
                setCampaignDialogTab(0);
                setEditingCampaign(null);
              } catch (err) {
                showSnackbar('Error al actualizar la ToolBox', 'error');
              } finally {
                setUpdatingCampaign(false);
              }
            }}
            variant="contained"
            disabled={!newCampaignData.campaign_name?.trim() || updatingCampaign}
          >
            {updatingCampaign ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            Guardar Cambios
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CampaignTracker;
